import { FC, PropsWithChildren, createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { Image, StyleSheet } from 'react-native';

import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';
import { Modal } from '@/feature/alerts/components/Modal';
import { isWeb } from '@/utils/constants-platform-specific';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';

const MODAL_ID = 'pickTypeBadgesEducation';

export type PickTypeBadgesEducationSheet = {
    showPickTypeBadgesEducationModal: () => void;
};

export const PickTypeBadgesEducationContext = createContext<PickTypeBadgesEducationSheet>({
    showPickTypeBadgesEducationModal: () => {},
});

export const usePickTypeBadgesEducation = () => {
    const context = useContext(PickTypeBadgesEducationContext);
    if (!context) {
        throw new Error('usePickTypeBadgesEducation must be used within a PickTypeBadgesEducationProvider');
    }
    return context;
};

/**
 * Provides the one-time educational bottom sheet pointing users to the Pick Type badges.
 * Mounted above the tab navigator so the sheet renders over the bottom tab bar.
 * Consumers (e.g. the lobby) call `showPickTypeBadgesEducationModal` to present it.
 */
export const PickTypeBadgesEducationProvider: FC<PropsWithChildren> = ({ children }) => {
    const modalRef = useRef<BottomSheetModalMethods | null>(null);

    const pickTypeBadgesEducationSheet = useMemo(
        () => ({
            showPickTypeBadgesEducationModal: () => {
                modalRef.current?.present();
            },
        }),
        []
    );

    const onGotItPress = useCallback(() => {
        modalRef.current?.dismiss();
    }, []);

    return (
        <CustomBottomSheetModalProvider>
            <PickTypeBadgesEducationContext.Provider value={pickTypeBadgesEducationSheet}>
                {children}
                <Modal id={MODAL_ID} modalRef={modalRef} backgroundColor={ds.colorValue.background.neutral.base}>
                    <Box paddingHorizontal={'s32'} paddingTop={'s48'}>
                        <Image
                            source={require('@/assets/images/pick_types_info_illustration.png')}
                            style={styles.illustration}
                            resizeMode={isWeb ? 'contain' : 'cover'}
                        />
                        <Text
                            variant={'headlineMedium'}
                            color={ds.colorToken.text.primary}
                            textAlign={'center'}
                            marginTop={'s24'}
                        >
                            Tap the Pick Type badges{'\n'}to learn more
                        </Text>
                        <Text
                            variant={'bodyMedium'}
                            color={ds.colorToken.text.secondary}
                            textAlign={'center'}
                            marginTop={'s8'}
                        >
                            Tap any Pick Type badge throughout the app to learn about the different Pick Types and how
                            they will impact your lineup.
                        </Text>
                        <Button
                            label={'Got it'}
                            hierarchy={'primary'}
                            onPress={onGotItPress}
                            style={styles.button}
                            testID={'pick-type-badges-education-got-it-button'}
                        />
                    </Box>
                </Modal>
            </PickTypeBadgesEducationContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

const styles = StyleSheet.create({
    illustration: {
        width: '100%',
        height: 208,
    },
    button: {
        marginTop: 48,
        marginBottom: 16,
    },
});
